import { Button, ComboBox, InlineNotification, Tooltip } from '@carbon/react';
import { Search, WarningFilled } from '@carbon/react/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  ownerDetailAtom,
  ownersAtom,
  selectedOwnerIndexAtom,
  selectedOwnerAtom,
} from './atoms';
import { OutputSkeleton } from '../../common/output_skeleton';
import { OutputListSkeleton } from '../../common/output_list_skeleton';
import style from '../calculation.module.scss';
import { Owner } from './owner';
import { CalculationEntity, TitleWorkspaceResponse } from 'types/api-responses';
type ForwardRefReturn<T, P> = React.ForwardRefExoticComponent<
  React.PropsWithoutRef<P> & React.RefAttributes<T>
>;
import { useEffect } from 'react';

const OwnerList = ({ entitiesHref }: { entitiesHref: string }) => {
  const [owners, fetchOwners] = useAtom(ownersAtom);
  const selectOwner = useSetAtom(selectedOwnerAtom);

  useEffect(() => {
    fetchOwners(entitiesHref);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitiesHref]);

  if (owners.loading) {
    return <OutputListSkeleton />;
  }
  return (
    <>
      <div className={style.searchField}>
        <ComboBox
          id={'owners-list'}
          titleText={''}
          items={owners.results}
          placeholder="Search a name..."
          itemToString={(data) => {
            return data ? data.entity.name : '';
          }}
          size="lg"
          selectedItem={undefined}
          onChange={({ selectedItem }) => {
            const index = owners.results.findIndex(
              (el) => el.entity.id === selectedItem?.entity.id
            );
            if (index >= 0) {
              selectOwner(index);
            }
          }}
        />
        <Search className={style.searchIcon} />
        <div className={style.assetSection}>Owners</div>
        <ul className={style.subTractList}>
          {owners.results.map((owner, index: number) => (
            <li key={index}>
              <OwnerButton owner={owner} idx={index} />
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

const OwnerButton = ({
  owner,
  idx,
}: {
  owner: CalculationEntity;
  idx: number;
}) => {
  const selectedIndex = useAtomValue(selectedOwnerIndexAtom);
  const selectOwner = useSetAtom(selectedOwnerAtom);
  const owners = useAtomValue(ownersAtom);
  const isSelected =
    owners.results[selectedIndex].entity.id === owner.entity.id;

  return (
    <div className={style.ownerButton}>
      <Button
        kind="ghost"
        size="sm"
        onClick={() => {
          selectOwner(idx);
        }}
        className={isSelected ? style.btnIsSelected : undefined}
      >
        {owner.entity.name}
      </Button>
      {owner.entity.deleted && (
        <Tooltip
          align="top-start"
          className={style.btnToolTip}
          renderIcon={WarningFilled as ForwardRefReturn<unknown, unknown>}
          iconDescription="Warning"
          iconName="warning-icon"
        >
          <div className={style.toolTipContent}>
            <h1>Entity Deleted</h1>
            <p>
              The title workspace must be recalculated to reflect entity
              alterations
            </p>
          </div>
        </Tooltip>
      )}
    </div>
  );
};

const SelectedOwner = ({
  titleWorkspace,
}: {
  titleWorkspace: TitleWorkspaceResponse;
}) => {
  const ownerDetails = useAtomValue(ownerDetailAtom);

  if (ownerDetails?.loading) return <OutputSkeleton />;
  if (!ownerDetails) return null;

  if (ownerDetails.data && 'error' in ownerDetails.data)
    return (
      <InlineNotification
        kind="error"
        title={ownerDetails.data.error}
        lowContrast
      />
    );
  if (!ownerDetails.data) return null;

  return (
    <Owner ownerDetails={ownerDetails.data} titleWorkspace={titleWorkspace} />
  );
};

export { OwnerList, SelectedOwner };
